import { useMemo } from "react";
import { Link } from "react-router-dom";
import { Users, Plus, Trash2 } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import TimePicker from "@/components/TimePicker";
import { ConfirmIcon, SmallInput } from "./helpers";

export function TalentSection({ team, items, onChange, confirmations }) {
  const talent = useMemo(() => team.filter((m) => m.member_type === "talent"), [team]);
  const assignedIds = items.map((i) => i.team_id);
  const available = talent.filter((m) => !assignedIds.includes(m.id));

  const add = (teamId) => {
    onChange([...items, { team_id: teamId, status: "", pickup: "", call_time: "", hmu: "" }]);
  };
  const update = (idx, key, value) => {
    const next = [...items];
    next[idx] = { ...next[idx], [key]: value };
    onChange(next);
  };
  const remove = (idx) => {
    onChange(items.filter((_, i) => i !== idx));
  };

  return (
    <section className="rounded-md border border-zinc-800 bg-zinc-900/60 p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Users className="w-4 h-4 text-amber-500" />
          <div className="text-overline text-zinc-400">Talent</div>
          <span className="text-xs text-zinc-500 font-mono">· {items.length}</span>
        </div>
        <div>
          {available.length > 0 ? (
            <Select onValueChange={add} value="">
              <SelectTrigger
                data-testid="cs-add-talent-trigger"
                className="bg-zinc-950 border-zinc-800 text-xs h-8 w-44"
              >
                <SelectValue placeholder="+ Add talent" />
              </SelectTrigger>
              <SelectContent className="bg-zinc-950 border-zinc-800 text-zinc-100">
                {available.map((m) => (
                  <SelectItem key={m.id} value={m.id}>
                    {m.name} — {m.role || "Talent"}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <Link
              to="/team"
              className="text-xs text-amber-500 hover:text-amber-400 inline-flex items-center gap-1"
            >
              <Plus className="w-3 h-3" /> Add talent to roster
            </Link>
          )}
        </div>
      </div>

      {items.length === 0 ? (
        <div className="text-sm text-zinc-500 italic">No talent assigned yet.</div>
      ) : (
        <div className="space-y-2">
          {items.map((it, idx) => {
            const person = team.find((m) => m.id === it.team_id);
            if (!person) return null;
            return (
              <div
                key={it.team_id}
                data-testid={`cs-talent-row-${it.team_id}`}
                className="grid grid-cols-12 gap-2 items-center p-2 rounded-sm bg-zinc-950 border border-zinc-800"
              >
                <div className="col-span-12 sm:col-span-3 min-w-0">
                  <div className="flex items-center gap-2">
                    <ConfirmIcon
                      email={person.email}
                      teamId={it.team_id}
                      confirmations={confirmations}
                    />
                    <div className="text-sm text-zinc-100 truncate">{person.name}</div>
                  </div>
                  <div className="text-xs text-zinc-500 truncate">{person.role}</div>
                </div>
                <div className="col-span-6 sm:col-span-2">
                  <TimePicker
                    value={it.call_time}
                    onChange={(v) => update(idx, "call_time", v)}
                    testIdPrefix={`talent-call-${it.team_id}`}
                    size="sm"
                  />
                </div>
                <SmallInput
                  testId={`talent-status-${it.team_id}`}
                  value={it.status}
                  placeholder="Status"
                  onChange={(v) => update(idx, "status", v)}
                  className="col-span-6 sm:col-span-2"
                />
                <SmallInput
                  testId={`talent-pickup-${it.team_id}`}
                  value={it.pickup}
                  placeholder="Pickup"
                  onChange={(v) => update(idx, "pickup", v)}
                  className="col-span-6 sm:col-span-2"
                />
                <SmallInput
                  testId={`talent-hmu-${it.team_id}`}
                  value={it.hmu}
                  placeholder="H/MU"
                  onChange={(v) => update(idx, "hmu", v)}
                  className="col-span-6 sm:col-span-2"
                />
                <button
                  onClick={() => remove(idx)}
                  data-testid={`remove-talent-${it.team_id}`}
                  className="col-span-12 sm:col-span-1 justify-self-end p-1.5 rounded-sm text-zinc-500 hover:text-rose-400"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}
